import { Check } from 'lucide-react';
import { StatusBadge } from './StatusBadge';

export interface WorkflowStep {
  key: string;
  label: string;
}

interface WorkflowStepperProps {
  /** The real, ordered happy-path stages for this record type - e.g. draft -> submitted -> under_review -> approved -> posted. */
  steps: WorkflowStep[];
  currentStatus: string;
  /**
   * Real statuses that represent a genuine detour from the happy
   * path (returned, rejected, reversed) rather than a step within
   * it. Shown as a distinct callout below the stepper, never
   * silently plotted at a fake position in the linear sequence -
   * that would misrepresent what actually happened to the record.
   */
  detourLabel?: string;
  /**
   * For a detour status only - the real index in `steps` the record
   * had genuinely reached before the detour happened (e.g. a
   * journal with reviewed_at set reached "Under Review" before
   * being returned, not "Submitted"). Required whenever detourLabel
   * is set; omitting it would show every step as not-yet-reached,
   * which is inaccurate for a record that demonstrably got further
   * than that before bouncing back.
   */
  reachedIndex?: number;
}

/**
 * The real workflow-visualization component the design system
 * audit found missing entirely - VConnect already tracks rich,
 * real lifecycle data on nearly every record (who reviewed it, who
 * approved it, when), but until now it was only ever shown as a
 * single flat status badge. This renders the actual position in the
 * real lifecycle, and is honest about branches: a record that was
 * returned or rejected shows exactly where it reached before that
 * happened, plus a clear, separate marker for the detour itself.
 */
export function WorkflowStepper({ steps, currentStatus, detourLabel, reachedIndex }: WorkflowStepperProps) {
  const currentIndex = steps.findIndex((s) => s.key === currentStatus);
  const isDetour = currentIndex === -1;
  // During a detour, progress renders up through the real reachedIndex (never assumed) - completed steps stay completed, nothing is falsely reset to "not yet reached."
  const effectiveIndex = isDetour ? (reachedIndex ?? -1) : currentIndex;

  return (
    <div className="workflow-stepper">
      <div className="workflow-stepper-track">
        {steps.map((step, i) => {
          const isCompleted = isDetour ? i <= effectiveIndex : i < currentIndex;
          const isCurrent = !isDetour && i === currentIndex;
          const stateClass = isCompleted ? 'is-completed' : isCurrent ? 'is-current' : 'is-upcoming';
          return (
            <div key={step.key} className={`workflow-step ${stateClass}`}>
              <div className="workflow-step-marker">
                {isCompleted ? <Check size={14} /> : <span className="workflow-step-dot" />}
              </div>
              <div className="workflow-step-label">{step.label}</div>
              {i < steps.length - 1 && <div className="workflow-step-connector" />}
            </div>
          );
        })}
      </div>
      {isDetour && (
        <div className="workflow-detour">
          <StatusBadge status={currentStatus} label={detourLabel} />
        </div>
      )}
    </div>
  );
}
